import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Choice, ChoiceForm, ChoiceSearchForm } from 'entities/choice'
import { useContext } from 'react'
import { MasterDataContext } from 'contexts/master_data_context'

type ChoicesResponse = PagingResponse & {
    results: Choice[]
}

export function useFetchChoicesApi(searchForm?: Form<ChoiceSearchForm>): IndexApiSet<ChoicesResponse> & { execute: () => void } {
    const apiPath = 'choices/'
    const api = useIndexApi<ChoicesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { perPage: 10000 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function usePostChoiceApi(): ApiSet<BaseResponse> & { execute: (form: Form<ChoiceForm>) => void } {
    const apiSet = usePostApi<BaseResponse, ChoiceForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ChoiceForm>) => {
        const apiPath = `choices/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutChoiceApi(): ApiSet<BaseResponse> & { execute: (form: Form<ChoiceForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ChoiceForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ChoiceForm>) => {
        const apiPath = `choices/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteChoiceApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `choices/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useBulkPutChoicesApi(): ApiSet<BaseResponse> & { execute: (form: Form<ChoiceForm[]>) => void } {
    const masterData = useContext(MasterDataContext)
    const apiSet = usePutApi<BaseResponse, ChoiceForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ChoiceForm[]>) => {
        const apiPath = `choices/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    useEffectSkipFirst(() => {
        if (apiSet.isSuccess()) {
            masterData.reloadChoices()
        }
    }, [apiSet.loading])

    return { ...apiSet, execute: execute }
}
